import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { apiError } from "../lib/format";
import { Plus, X, Tag as TagIcon, Check } from "@phosphor-icons/react";

const COLOR_CLASSES = {
  forest: "bg-forest/10 text-forest border-forest/30",
  burnt: "bg-burnt/10 text-burnt border-burnt/30",
  success: "bg-success/10 text-success border-success/30",
  warn: "bg-warn/10 text-warn border-warn/30",
  danger: "bg-danger/10 text-danger border-danger/30",
  ink: "bg-ink/10 text-ink border-ink/30",
  slate: "bg-inkMuted/10 text-inkMuted border-inkMuted/30",
};
const COLOR_SWATCH = {
  forest: "bg-forest", burnt: "bg-burnt", success: "bg-success",
  warn: "bg-warn", danger: "bg-danger", ink: "bg-ink", slate: "bg-inkMuted",
};
export const COLORS = Object.keys(COLOR_CLASSES);

/** Display a list of tags as readonly pills (from tagIds + catalog). */
export function TagsList({ tagIds = [], catalog = [], size = "sm", onRemove = null }) {
  const tags = tagIds
    .map((id) => catalog.find((t) => t.id === id))
    .filter(Boolean);
  if (tags.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1" data-testid="tags-list">
      {tags.map((t) => (
        <span
          key={t.id}
          data-testid={`tag-chip-${t.id}`}
          className={`inline-flex items-center gap-1 px-2 ${size === "xs" ? "text-[10px] py-0" : "text-xs py-0.5"} font-mono uppercase tracking-widest border ${COLOR_CLASSES[t.color] || COLOR_CLASSES.slate}`}
        >
          {t.name}
          {onRemove && (
            <button
              onClick={() => onRemove(t.id)}
              className="hover:opacity-80"
              data-testid={`tag-remove-${t.id}`}
            >
              <X size={10} weight="bold" />
            </button>
          )}
        </span>
      ))}
    </div>
  );
}

/**
 * Full tag picker for a user. Fetches catalog + allows create & assign/remove.
 * Props: userId, currentTags (array of ids), onChange(newTagIds) called after server ops.
 */
export function TagPicker({ userId, currentTags = [], onChange, compact = false }) {
  const [catalog, setCatalog] = useState([]);
  const [open, setOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("forest");

  const loadCatalog = async () => {
    try {
      const { data } = await api.get("/tags");
      setCatalog(data);
    } catch {}
  };

  useEffect(() => { loadCatalog(); }, []);

  const assign = async (tagId) => {
    try {
      await api.post(`/users/${userId}/tags/${tagId}`);
      const next = Array.from(new Set([...(currentTags || []), tagId]));
      onChange?.(next);
      toast.success("Etiqueta asignada");
    } catch (e) { toast.error(apiError(e)); }
  };

  const unassign = async (tagId) => {
    try {
      await api.delete(`/users/${userId}/tags/${tagId}`);
      onChange?.((currentTags || []).filter((id) => id !== tagId));
      toast.success("Etiqueta quitada");
    } catch (e) { toast.error(apiError(e)); }
  };

  const createAndAssign = async () => {
    if (!newName.trim()) return;
    try {
      const { data } = await api.post("/tags", { name: newName.trim(), color: newColor });
      await api.post(`/users/${userId}/tags/${data.id}`);
      const next = Array.from(new Set([...(currentTags || []), data.id]));
      onChange?.(next);
      setNewName("");
      setNewColor("forest");
      loadCatalog();
      toast.success("Etiqueta creada y asignada");
    } catch (e) { toast.error(apiError(e)); }
  };

  const available = catalog.filter((t) => !(currentTags || []).includes(t.id));

  return (
    <div className="relative" data-testid="tag-picker">
      <div className="flex items-center gap-2 flex-wrap">
        <TagsList tagIds={currentTags} catalog={catalog} onRemove={unassign} />
        <button
          onClick={() => setOpen((v) => !v)}
          className={`inline-flex items-center gap-1 px-2 ${compact ? "text-[10px] h-6" : "text-xs h-7"} font-mono uppercase tracking-widest border border-dashed border-hairline text-inkMuted hover:text-forest hover:border-forest`}
          data-testid="tag-picker-toggle"
        >
          <Plus size={12} weight="bold" /> Etiqueta
        </button>
      </div>

      {open && (
        <div className="absolute top-full left-0 mt-2 w-72 bg-surface border border-hairline shadow-lg z-30" data-testid="tag-picker-dropdown">
          <div className="px-3 py-2 border-b border-hairline flex items-center justify-between">
            <div className="label-mono flex items-center gap-1"><TagIcon size={12} weight="duotone" /> Asignar etiqueta</div>
            <button onClick={() => setOpen(false)} className="p-1 hover:bg-bg" data-testid="tag-picker-close"><X size={12} /></button>
          </div>

          <div className="max-h-[180px] overflow-auto">
            {available.length === 0 ? (
              <div className="p-3 text-xs text-inkMuted text-center">Sin etiquetas disponibles.</div>
            ) : (
              available.map((t) => (
                <button
                  key={t.id}
                  onClick={() => { assign(t.id); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-bg border-b border-hairline"
                  data-testid={`tag-assign-${t.id}`}
                >
                  <span className={`w-2 h-2 ${COLOR_SWATCH[t.color] || COLOR_SWATCH.slate}`} />
                  <span className="text-sm">{t.name}</span>
                </button>
              ))
            )}
          </div>

          <div className="p-3 border-t border-hairline space-y-2">
            <div className="label-mono">Nueva etiqueta</div>
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Ej: VIP, Moroso, Prospecto"
              className="w-full h-9 px-2 border border-hairline bg-bg text-sm"
              data-testid="tag-new-name"
            />
            <div className="flex gap-1">
              {COLORS.map((c) => (
                <button
                  key={c}
                  onClick={() => setNewColor(c)}
                  className={`w-6 h-6 ${COLOR_SWATCH[c]} ${newColor === c ? "ring-2 ring-offset-1 ring-ink" : ""}`}
                  title={c}
                  data-testid={`tag-color-${c}`}
                />
              ))}
            </div>
            <button
              onClick={createAndAssign}
              disabled={!newName.trim()}
              className="w-full h-9 bg-forest text-bg text-xs font-semibold flex items-center justify-center gap-1 disabled:opacity-50"
              data-testid="tag-create-assign"
            >
              <Check size={12} weight="bold" /> Crear y asignar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
